



















































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Enums
import { DataModules } from "@/store/modules/data";
import { ProductTypes } from "@/data/enums/products";
import { ActionTypes } from "@/data/enums";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Types
import { IState } from "@/store";
import { IProductCategory } from "@/models/products";
import { IBrand } from "@/models/brands";

@Component({
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.brand.id;
      },
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesModule,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/rootCategoriesScope`](
          this.brandId,
          this.productTypes
        );
      },
      sortableCategoriesScope(state: IState, getters) {
        return getters[`${this.sortableCategoriesModule}/scope`](
          this.brandId,
          this.isOption
            ? "product_options"
            : this.isAttribute
            ? "product_attributes"
            : "products"
        );
      }
    })
  }
})
export default class CategoryPicker extends Mixins(RequestPromises) {
  // Map State
  public brandId!: IBrand["id"];
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];
  public sortableCategoriesScope!: string;

  @Prop({ required: true })
  public value!: IProductCategory["id"];

  @Prop({ required: true })
  public isProcessing!: boolean;

  @Prop({ required: true })
  public productTypes!: ProductTypes[];

  @Prop({ default: null })
  public currentCategoryId!: IProductCategory["id"];

  @Prop({ default: 1 })
  public numberOfModals!: number;

  @Prop({ default: false })
  public isParent!: boolean;

  @Prop({ default: false })
  public isStaged!: boolean;

  public categoriesModule: string = DataModules.CATALOGUE_CATEGORIES;
  public sortableCategoriesModule = "sortableCategories";

  public preSelected!: IProductCategory["id"];

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public created() {
    if (this.contentAvailable) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public async loadData() {
    try {
      await this.getRootCategories();

      if (!this.categoryId) {
        this.$set(
          this,
          "categoryId",
          this.$_.get(this.rootCategories, "[0].id", null)
        );
      }
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }

    this.isReloading = false;
    this.isLoading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public get categoryId() {
    return this.value;
  }

  public set categoryId(newCategoryId: IProductCategory["id"]) {
    this.$emit("input", newCategoryId);
  }

  public async onCategoryChange(value: any) {
    if (value !== "new") {
      return;
    }

    this.isLoading = true;
    this.preSelected = this.categoryId;

    this.$nextTick(() => {
      this.categoryId = this.preSelected;
    });

    const result = await this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/openAddEditCategoryModal`,
      {
        productTypes: this.productTypes,
        brandId: this.brandId,
        actionType: ActionTypes.CREATE,
        numberOfModals: this.numberOfModals + 1
      }
    );

    if (!result.cancelled) {
      this.categoryId = this.$_.get(result, "results.id", null);
    }

    this.isLoading = false;
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get i18n() {
    return {
      fieldLabel: this.isOption
        ? this.$t(`_.${this.isParent ? "parent_" : ""}product_options_category`)
        : this.isAttribute
        ? this.$t(
            `_.${this.isParent ? "parent_" : ""}product_attributes_category`
          )
        : this.$t(`_.${this.isParent ? "parent_" : ""}product_category`),
      fieldPlaceholder: this.$t("_sentence.please_select", {
        object: this.isOption
          ? this.$t("_.product_options_category")
              .toString()
              .toLowerCase()
          : this.isAttribute
          ? this.$t("_.product_attributes_category")
              .toString()
              .toLowerCase()
          : this.$t("_.product_category")
              .toString()
              .toLowerCase()
      }),
      categoriesName: this.isOption
        ? this.$t("_.product_options_categories").toString()
        : this.isAttribute
        ? this.$t("_.product_attributes_categories").toString()
        : this.$t("_.product_categories").toString(),
      fieldId: this.isOption
        ? "product_options_category"
        : this.isAttribute
        ? "product_attributes_category"
        : "product_category"
    };
  }

  public getRootCategories(forceReload = false) {
    return this.$store.dispatch(`data/${this.categoriesModule}/list`, {
      productTypes: this.productTypes,
      scope: this.rootCategoriesScope,
      params: this.$_.merge(
        {
          order: "order",
          limit: 0,
          "filter[level]": 1
        },
        this.isStaged
          ? {
              with_staged_imports: 1
            }
          : {}
      ),
      vm: this,
      ignoreStored: forceReload
    });
  }

  public get flattenCategory() {
    return this.$_.find(this.flattenCategories, ["id", this.categoryId]);
  }

  public get flattenCategories() {
    return this.flatCategories(this.rootCategories, 0);
  }

  public itemsCounter() {
    return this.isOption
      ? "_plurals.product_options"
      : this.isAttribute
      ? "_plurals.product_attributes"
      : "_plurals.products";
  }

  public flatCategories(categories, level) {
    let flattenCategories: any[] = [];
    const newLevel = level + 1;

    this.$_.forEach(categories, category => {
      flattenCategories.push({
        id: category.id,
        name: category.name,
        level
      });

      flattenCategories = this.$_.concat(
        flattenCategories,
        this.flatCategories(category.subcategories, newLevel)
      );
    });

    return flattenCategories;
  }

  public get contentAvailable() {
    return this.isOption
      ? this.$userCan("list_product_options_categories")
      : this.isAttribute
      ? this.$userCan("list_product_attributes_categories")
      : this.$userCan("list_product_categories");
  }

  public get guardFunctionalities() {
    return this.isOption
      ? ["list_product_options_categories"]
      : this.isAttribute
      ? ["list_product_attributes_categories"]
      : ["list_product_categories"];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesModule,
      scope: this.rootCategoriesScope,
      vm: this
    });
  }
}
